import {useEffect, useRef} from 'react';

import {AppRoute} from '../../../models/AppRoute';
import * as RootNavigation from '../../../navigation/RootNavigation';
import {useStores} from '../../../stores/use-stores';
import type {CheckoutStep} from '../UnifiedCheckout/types';

export interface UseCheckoutSessionParams {
  /** Current checkout step - determines redirect behavior */
  step: CheckoutStep;
  /** Skip login reload (default: false) */
  skipLoginReload?: boolean;
  /** Skip empty cart redirect - use for cart step which shows EmptyCart instead (default: false) */
  skipEmptyCartRedirect?: boolean;
}

export interface CheckoutSessionResult {
  /** Whether the session is ready (not redirecting, not loading) */
  isReady: boolean;
  /** Whether user is logged in */
  isLoggedIn: boolean;
}

/**
 * Manages checkout session lifecycle:
 * 1. Reloads cart when user logs in (for member pricing)
 * 2. Redirects to cart if order is empty (for non-cart steps)
 *
 * Consolidates duplicated logic from:
 * - CartScreenLoyaltyEnabled (login reload)
 * - CheckoutScreenLoyaltyEnabled (login reload + empty redirect)
 * - CheckoutReviewScreen (login reload + empty redirect)
 * - CheckoutPaymentScreen (empty redirect)
 * - UnifiedCheckoutScreen (was MISSING both - now fixed)
 *
 * Uses RootNavigation for cross-navigator navigation per React Navigation v7 best practices.
 */
export function useCheckoutSession({
  step,
  skipLoginReload = false,
  skipEmptyCartRedirect = false,
}: UseCheckoutSessionParams): CheckoutSessionResult {
  const {cartStore, userStore} = useStores();
  const isLoggedIn = userStore.isLoggedIn;

  // Track previous login state to detect transitions
  // Initialize with current value so first mount doesn't trigger reload for already-logged-in users
  const prevIsLoggedInRef = useRef(isLoggedIn);

  // Effect 1: Reload cart when user logs in (for member pricing)
  // IMPORTANT: Only triggers on login TRANSITION (false → true), not on mount when already logged in.
  // This preserves gift card state when navigating between checkout screens.
  // DIS-678: Previously ran on every mount when logged in, clearing zenGiftCardsUserPaymentInstruments
  useEffect(() => {
    const wasLoggedIn = prevIsLoggedInRef.current;
    prevIsLoggedInRef.current = isLoggedIn;

    // Only reload when transitioning from logged out to logged in
    if (!skipLoginReload && !wasLoggedIn && isLoggedIn) {
      cartStore.fullyReloadOrderFromMFL();
    }
  }, [isLoggedIn, cartStore, skipLoginReload]);

  // Effect 2: Redirect to cart if order is empty (for non-cart steps)
  // Track emptiness as a boolean to avoid re-running when array reference changes but content doesn't
  const hasOrderLines = !!cartStore.currentOrder?.order_lines?.length;

  useEffect(() => {
    if (skipEmptyCartRedirect) return;
    if (step === 'cart') return; // Cart step shows EmptyCart component instead

    if (!cartStore.isLoadingOrder && !hasOrderLines) {
      // Use RootNavigation for cross-navigator navigation (React Navigation v7 best practice)
      // Checkout screens are in ROOT_CHECKOUT_FLOW, cart is in ROOT → MAIN
      RootNavigation.navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {screen: AppRoute.CART},
      });
    }
  }, [hasOrderLines, cartStore.isLoadingOrder, skipEmptyCartRedirect, step]);

  const isReady =
    !cartStore.isLoadingOrder && (step === 'cart' || !!cartStore.currentOrder?.order_lines?.length);

  return {
    isReady,
    isLoggedIn,
  };
}
